import { cancel, put, select, takeEvery, takeLatest, call, fork } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import copy from 'copy-to-clipboard';
import * as http from 'utils/http';
import _ from 'lodash';
import config from 'config';

import { TrackeableEvent, sendEventToSegment } from 'redux/modules/analytics';

import {
  CHANGE_COST_OPEN_MODAL,
  CHANGE_COST_CLOSE_MODAL,
  CHANGE_COST_SAVE,
  JOURNEY_MODAL_OPEN,
  VIEW_VALID_VEHICLES,
  SHIPPER_ORDER_UPDATE_START,
  JOURNEY_UPDATE_START,
  JOURNEY_UPDATE_END,
  CREATE_CONVERSATION,
} from 'redux/modules/analytics/events';

import { ShipperOrderTracking, JourneyTracking } from 'redux/modules/analytics/task-measure';

import {
  goToVehiclesWithFilters,
  openPage,
  openEditOrder,
} from 'redux/modules/traffic-common/effects';

import {
  assignJourney as assignJourneyRequest,
  reassignJourney as reassignJourneyRequest,
  unassignJourney as unassignJourneyRequest,
  reviewJourney as reviewJourneyRequest,
  unReviewJourney as unReviewJourneyRequest,
  fetchJourney,
  updateStopTimes as updateStopTimesRequest,
} from 'redux/modules/journeys/http';

import {
  assignShipperOrder as assignShipperOrderRequest,
  releaseShipperOrder as releaseShipperOrderRequest,
  launchShipperOrder as launchShipperOrderRequest,
} from 'redux/modules/shipper-orders/http';

import { openShipperOrderDetail } from 'redux/modules/shipper-orders/effects';

import { createJourneyLog, openManualLog } from 'redux/modules/log/effects';
import { setSelfChangedData$ } from 'redux/modules/realtime/effects';
import { openFormWithShipperOrder } from 'redux/modules/shipper-order-new/effects';

import { computeProblematic } from 'redux/modules/journeys/computed';
import { mapJourneyForComputedProperties } from 'redux/modules/journeys/computed';

import { displayError } from 'redux/modules/error-handler/effects';
import { JOURNEY_UNASSIGN } from 'redux/modules/error-handler/constants';

import templates from 'modules/Common/Copy/Templates';

import { takeAtomicUpdate } from 'redux/modules/realtime/atomic-updates';

import { logChangesGenerator } from 'redux/modules/log/log-changes';

import {
  setFetching,
  setData,
  setReviewing,
  setDecisionMode,
  setActionLoading,
  setShipperOrderAssignmentId,
  setError,
  setSubmodal,
  setMinervaInfo,
  setUnassigning,
  setUpdatingTimes,
} from './duck';

import { getState, getMe, getShipperOrderAssignmentId } from './selectors';

// @effect Action
const detailMounted$ = 'detail/detailMounted$';
const openSubmodal$ = 'detail/openSubmodal$';
const detailUnmounted$ = 'detail/detailUnmounted$';
const reviewJob$ = 'detail/reviewJob$';
const copyJobDetails$ = 'detail/copyJobDetails$';
const viewValidVehicles$ = 'detail/viewValidVehicles$';
const addManualEntryLog$ = 'detail/addManualEntryLog$';
const openShipper$ = 'detail/openShipper$';
const openUser$ = 'detail/openUser$';
const openCarrier$ = 'detail/openCarrier$';
const openDriver$ = 'detail/openDriver$';
const assignJourney$ = 'detail/assignJourney$';
const assignShipperOrderWithBreak$ = 'detail/assignShipperOrderWithBreak$';
const closeShipperOrderAssignment$ = 'detail/closeShipperOrderAssignment$';
const unassignJourney$ = 'detail/unassignJourney$';
const goToPreviousJob$ = 'detail/goToPreviousJob$';
const viewCarrierVehicles$ = 'detail/viewCarrierVehicles$';
const editOrder$ = 'detail/editOrder$';
const repeatOrder$ = 'detail/repeatOrder$';
const showDecisionModal$ = 'detail/showDecisionModal$';
const removeAndAssign$ = 'detail/removeAndAssign$';
const removeAndLaunch$ = 'detail/removeAndLaunch$';
const removeAndRelease$ = 'detail/removeAndRelease$';
const openBaseModalWithUpdate$ = 'detail/openBaseModalWithUpdate$';
const fetchMinervaInfo$ = 'detail/fetchMinervaInfo$';

const closeAssigmentModal$ = 'detail/closeAssigmentModal$';
const assignDriver$ = 'detail/assignDriver$';
const changeDriver$ = 'detail/changeDriver$';
const changeVehicle$ = 'detail/changeVehicle$';
const selectVehicle$ = 'detail/selectVehicle$';

const openEditCost$ = 'detail/openEditCost$';
const openViewCost$ = 'detail/openViewCost$';
const closeEditCost$ = 'detail/closeEditCost$';
const saveEditCost$ = 'detail/saveEdit$';
const refreshJobDetail$ = 'detail/refreshJobDetail$';
const updateStopTimes$ = 'detail/updateStopTimes$';

const openOrCreateChat$ = 'detail/openOrCreateChat$';

// @effect action creators
export const detailMounted = (payload) => ({
  type: detailMounted$,
  payload,
});

export const openSubmodal = (payload) => ({
  type: openSubmodal$,
  payload,
});

export const openBaseModal = () => ({
  type: openSubmodal$,
  payload: {
    name: '',
  },
});

export const openBaseModalWithUpdate = (journey) => ({
  type: openBaseModalWithUpdate$,
  payload: journey,
});

export const detailUnmounted = (payload) => ({
  type: detailUnmounted$,
  payload,
});

export const reviewJob = (payload) => ({
  type: reviewJob$,
  payload,
});

export const copyJobDetails = () => ({
  type: copyJobDetails$,
  payload: {
    type: 'jobDetail',
  },
});

export const copyAssignationMessage = () => ({
  type: copyJobDetails$,
  payload: {
    type: 'assignation',
  },
});

export const viewValidVehicles = () => ({
  type: viewValidVehicles$,
});

export const addManualEntryLog = () => ({
  type: addManualEntryLog$,
});

export const openShipper = ({ shipper_id }) => ({
  type: openShipper$,
  payload: {
    type: 'shipper',
    shipper_id,
  },
});

export const openUser = ({ shipper_id, user_id }) => ({
  type: openUser$,
  payload: {
    type: 'shipper_user',
    shipper_id,
    user_id,
  },
});

export const openCarrier = ({ carrier_id }) => ({
  type: openCarrier$,
  payload: {
    type: 'carrier',
    carrier_id,
  },
});

export const openDriver = ({ carrier_id, driver_id }) => ({
  type: openDriver$,
  payload: {
    type: 'driver',
    carrier_id,
    driver_id,
  },
});

export const openAssignment = () => ({
  type: openSubmodal$,
  payload: {
    name: 'assignment',
  },
});

export const assignDriver = (payload) => ({
  type: assignDriver$,
  payload,
});

export const closeAssigmentModal = (payload) => ({
  type: closeAssigmentModal$,
  payload,
});

export const changeDriver = (payload) => ({
  type: changeDriver$,
  payload,
});

export const changeVehicle = (payload) => ({
  type: changeVehicle$,
  payload,
});

export const selectVehicle = (payload) => ({
  type: selectVehicle$,
  payload,
});

export const openUploadCargoManifest = ({ stop_id, cargo_id }) => ({
  type: openSubmodal$,
  payload: {
    name: 'uploadcargo',
    params: { stop_id, cargo_id },
  },
});

export const openDeleteCargoManifest = ({ stop_id, cargo_id }) => ({
  type: openSubmodal$,
  payload: {
    name: 'deletecargo',
    params: { stop_id, cargo_id },
  },
});

// TODO extract this to a isolated use case.
export const opendEditCost = () => ({
  type: openEditCost$,
  payload: {
    name: 'edit_cost',
  },
});

export const openViewCost = () => ({
  type: openViewCost$,
  payload: {
    name: 'view_cost',
  },
});

export const closeEditCost = (payload) => ({
  type: closeEditCost$,
  payload,
});

export const saveEditCost = (payload) => ({
  type: saveEditCost$,
  payload,
});

export const assignJourney = ({ driver, vehicle, vehicles }) => ({
  type: assignJourney$,
  payload: {
    driver,
    vehicle,
    vehicles,
  },
});

export const assignShipperOrderWithBreak = ({ driver, vehicle, vehicles }) => ({
  type: assignShipperOrderWithBreak$,
  payload: {
    driver,
    vehicle,
    vehicles,
  },
});

export const closeShipperOrderAssignment = (payload) => ({
  type: closeShipperOrderAssignment$,
  payload,
});

export const unassignJourney = () => ({
  type: unassignJourney$,
  payload: {
    launchOffer: true,
  },
});

export const removeAndAssign = ({ id }) => ({
  type: removeAndAssign$,
  payload: { id },
});

export const removeAndLaunch = ({ id }) => ({
  type: removeAndLaunch$,
  payload: { id },
});

export const removeAndRelease = ({ id }) => ({
  type: removeAndRelease$,
  payload: { id },
});

export const editOrder = (id, redirectAfterSave) => ({
  type: editOrder$,
  payload: { id, redirectAfterSave },
});

export const repeatOrder = (id) => ({
  type: repeatOrder$,
  payload: { id },
});

export const goToPreviousJob = (id) => ({
  type: goToPreviousJob$,
  payload: {
    id,
  },
});

export const viewCarrierVehicles = ({ carrier_id }) => ({
  type: viewCarrierVehicles$,
  payload: {
    type: 'view_carrier_vehicles',
    carrier_id,
  },
});

export const showUnassignDecisionModal = () => ({
  type: showDecisionModal$,
  payload: {
    mode: 'UNASSIGN',
  },
});

export const showRemoveOrderDecisionModal = ({ id }) => ({
  type: showDecisionModal$,
  payload: {
    mode: 'REMOVE_ORDER',
    id,
  },
});

export const closeDecisionModal = () => ({
  type: showDecisionModal$,
  payload: {
    mode: '',
  },
});

export const fetchMinervaInfo = () => ({
  type: fetchMinervaInfo$,
});

export const openOrCreateChat = ({ journeyId, driverId, conversationId, type }) => ({
  type: openOrCreateChat$,
  payload: {
    journeyId,
    driverId,
    conversationId,
    type,
  },
});

export const refreshJobDetail = ({ journeyId }) => ({
  type: refreshJobDetail$,
  payload: {
    journeyId,
  },
});

export const updateStopTimes = ({ journeyId, stopId, timesEdited }) => ({
  type: updateStopTimes$,
  payload: {
    journeyId,
    stopId,
    timesEdited,
  },
});

// Workers

export const computeJourney = async (journey) => {
  return mapJourneyForComputedProperties(computeProblematic(journey));
};

function* callAndSetData(...params) {
  const journey = yield call(getDetailData);
  const newJourney = yield call(...params);

  if (journey?.id !== newJourney?.id) {
    return;
  }

  const computedJourney = yield call(computeJourney, newJourney);

  yield put(setData(computedJourney));

  return computedJourney;
}

function* getDetailData() {
  const { data } = yield select(getState);
  return data;
}

let parallelTasks = [];
let mountDetailAction;
let pollingTask;

const openJourneyDetailTrackeableEvent = new TrackeableEvent();

function* handleMounted({ payload }) {
  mountDetailAction = yield fork(mountDetail, { payload });
}

export function* mountDetail({ payload }) {
  const { id: journeyId } = payload;

  if (pollingTask) yield cancel(pollingTask);

  yield call(openJourneyDetailTrackeableEvent.start, JOURNEY_MODAL_OPEN, {
    journeyId,
  });

  yield put(setError(null));
  yield put(setFetching(true));

  try {
    const journey = yield call(fetchJourney, journeyId);
    const computedJourney = yield call(computeJourney, journey);
    yield put(setData(computedJourney));
    pollingTask = yield fork(pollingJobDetailTask, { journeyId });

    parallelTasks = [
      yield takeLatest(reviewJob$, toggleReview),
      yield takeLatest(assignJourney$, handleAssignment),
      yield takeLatest(unassignJourney$, handleUnassignment),
      yield takeLatest(removeAndAssign$, handleRemoveAndAssign),
      yield takeLatest(removeAndLaunch$, handleRemoveAndLaunch),
      yield takeLatest(removeAndRelease$, handleRemoveAndRelease),
    ];
  } catch (err) {
    yield put(setError(err));
  } finally {
    yield put(setFetching(false));
    yield call(openJourneyDetailTrackeableEvent.end, JOURNEY_MODAL_OPEN, {
      journeyId,
    });
  }
}

function* pollingJobDetailTask({ journeyId }) {
  try {
    while (true) {
      yield call(delay, 50000);
      const journey = yield call(fetchJourney, journeyId);
      const computedJourney = yield call(computeJourney, journey);
      yield put(setData(computedJourney));
    }
  } catch (e) {
    console.error(e);
  }
}

function* handleRefreshJobDetail({ payload }) {
  const { journeyId } = payload;
  const journey = yield call(fetchJourney, journeyId);
  const computedJourney = yield call(computeJourney, journey);
  yield put(setData(computedJourney));
}

export function* handleUpdateStopTimes({ payload }) {
  const { journeyId, stopId, timesEdited } = payload;
  yield put(setUpdatingTimes(true));
  try {
    yield call(updateStopTimesRequest, journeyId, stopId, timesEdited);
    const journey = yield call(fetchJourney, journeyId);
    const computedJourney = yield call(computeJourney, journey);
    yield put(setData(computedJourney));
    yield put(setUpdatingTimes(false));
  } catch (e) {
    console.error('Error handleUpdateStopTimes ', e);
  } finally {
    yield put(setUpdatingTimes(false));
  }
}

function* submodal({ payload }) {
  const { name, params } = payload;
  yield put(setSubmodal(name, params));
}

function* openBaseAndUpdate({ payload }) {
  const journey = payload;
  const computedJourney = yield call(computeJourney, journey);
  yield put(setData(computedJourney));
  yield put(openBaseModal());
}

function* handledetailUnmounted() {
  if (mountDetailAction) yield cancel(mountDetailAction);
  if (pollingTask) yield cancel(pollingTask);
  yield put(setData());
  for (let i in parallelTasks) {
    yield cancel(parallelTasks[i]);
  }

  yield put(setFetching(true));
  yield put(setError(null));
}

function* toggleReview({ payload }) {
  yield put(setReviewing(true));
  const journey = yield call(getDetailData);
  try {
    journey.is_reviewed
      ? yield callAndSetData(unReviewJourneyRequest, journey)
      : yield callAndSetData(reviewJourneyRequest, journey);
  } catch (err) {
    console.error(err);
  } finally {
    yield put(setReviewing(false));
  }
}

function* copyToClipboard({ payload }) {
  const { type } = payload;
  const journey = yield call(getDetailData);
  const me = yield select(getMe);

  try {
    const offerLink = _.get(me, 'country_settings.links.offer', '');
    yield call(copy, templates[type](journey, offerLink));
  } catch (err) {
    console.error(err);
  }
}

function* goToValidVehicles() {
  const journey = yield call(getDetailData);
  yield call(goToVehiclesWithFilters, journey);
  yield call(sendEventToSegment, VIEW_VALID_VEHICLES, { journeyId: journey.id });
}

function* handleAddManualLogEntry() {
  const journey = yield call(getDetailData);
  yield put(
    openManualLog({
      journeyId: journey.id,
    })
  );
}

function* createAssignmentLog({ journey, newJourney }) {
  const oldVehicles = _.get(journey, 'vehicles');
  const oldTrailer = oldVehicles.find((vehicle) => vehicle.vehicle_type === 'trailer');

  const newVehicles = _.get(newJourney, 'vehicles');
  const newTrailer = newVehicles.find((vehicle) => vehicle.vehicle_type === 'trailer');

  const action = 'ASSIGN_DRIVER';

  const changes = logChangesGenerator.get(action, {
    carrier_id_before: _.get(journey, 'carrier.id'),
    driver_id_before: _.get(journey, 'driver.id'),
    carrier_name_before: _.get(journey, 'carrier.name'),
    driver_name_before: _.get(journey, 'driver.contact_name'),
    vehicle_plate_number_before: _.get(journey, 'vehicle.plate_number'),
    trailer_plate_number_before: _.get(oldTrailer, 'plate_number'),

    carrier_id_after: _.get(newJourney, 'carrier.id'),
    driver_id_after: _.get(newJourney, 'driver.id'),
    carrier_name_after: _.get(newJourney, 'carrier.name'),
    driver_name_after: _.get(newJourney, 'driver.contact_name'),
    vehicle_plate_number_after: _.get(newJourney, 'vehicle.plate_number'),
    trailer_plate_number_after: _.get(newTrailer, 'plate_number'),
  });

  yield put(
    createJourneyLog({
      action,
      journeyId: journey.id,
      changes,
    })
  );
}

function* handleAssignment({ payload }) {
  const journey = yield call(getDetailData);
  const { driver, vehicle, vehicles } = payload;
  const { status } = journey;
  const isPending = status === 'PENDING_ASSIGNMENT';

  if (isPending && !journey.driver) {
    try {
      const newJourney = yield callAndSetData(
        assignJourneyRequest,
        journey,
        driver,
        vehicle,
        vehicles
      );

      yield call(createAssignmentLog, { journey, newJourney });

      yield put(openBaseModal());

      yield call(JourneyTracking.end, journey.id, JOURNEY_UPDATE_END, {
        actionType: 'assign',
      });
    } catch (err) {
      console.error(err);
    }
    return;
  }

  try {
    const newJourney = yield callAndSetData(
      reassignJourneyRequest,
      journey,
      driver,
      vehicle,
      vehicles
    );
    yield put(openBaseModal());
    yield call(createAssignmentLog, { journey, newJourney });
    yield call(JourneyTracking.end, journey.id, JOURNEY_UPDATE_END, {
      actionType: 'reassign',
    });
  } catch (err) {
    yield call(http.getError, err);
  }
}

function* handleUnassignment({ payload }) {
  const { launchOffer = true } = payload;

  const journey = yield call(getDetailData);
  const { id } = journey || {};

  if (!id) {
    return;
  }

  yield put(setActionLoading(true));
  yield put(setUnassigning(true));
  try {
    yield callAndSetData(unassignJourneyRequest, id, { launch_offer: launchOffer });
    yield put(openBaseModal());

    const action = 'UNASSIGN_DRIVER';
    const changes = logChangesGenerator.get(action, {
      driver_contact_name: _.get(journey, 'driver.contact_name'),
    });

    yield put(
      createJourneyLog({
        action,
        journeyId: journey.id,
        editableAfterCreation: true,
        changes,
      })
    );
  } catch (err) {
    yield put(displayError(err, JOURNEY_UNASSIGN));
    console.error(err);
  } finally {
    yield put(setActionLoading(false));
    yield put(setUnassigning(false));
    yield put(closeDecisionModal());
  }
}

function* handleDecisionModal({ payload }) {
  const { mode, id } = payload;
  yield put(setDecisionMode({ mode, id }));
}

function* handleRemoveAndAssign({ payload }) {
  const { id } = payload;
  yield put(setShipperOrderAssignmentId(id));
}

function* handleRemoveAndLaunch({ payload }) {
  const journey = yield call(getDetailData);
  yield put(setActionLoading(true));
  try {
    const shipperOrder = (journey.shipper_orders || []).find(
      (shipperOrder) => shipperOrder.id === payload.id
    );
    yield call(launchShipperOrderRequest, shipperOrder, { break_journey: true });
    yield put(closeDecisionModal());
  } catch (err) {
    yield call(http.getError, err);
  } finally {
    yield put(setActionLoading(false));
  }
}

function* handleRemoveAndRelease({ payload }) {
  const journey = yield call(getDetailData);
  const { id } = payload;
  yield put(setActionLoading(true));
  try {
    const shipperOrder = (journey.shipper_orders || []).find(
      (shipperOrder) => shipperOrder.id === id
    );
    const newShipperOrder = yield call(releaseShipperOrderRequest, shipperOrder, {
      break_journey: true,
      journey_id: journey.id,
    });

    if (!(newShipperOrder.journeys || []).length) {
      yield put(openShipperOrderDetail({ id: newShipperOrder.id }));
    }
    yield put(closeDecisionModal());
  } catch (err) {
    yield call(http.getError, err);
  } finally {
    yield put(setActionLoading(false));
  }
}

function* handleSelfChanged({ payload }) {
  const journey = yield call(getDetailData) || {};

  if (!journey || !journey.id) {
    return;
  }

  const newJourney = payload;

  if (!newJourney) {
    return;
  }

  const computedJourney = yield call(computeJourney, newJourney);
  yield put(setData(computedJourney));
}

function* handleAssignWithBreak({ payload }) {
  const journey = yield call(getDetailData);
  const shipperOrderId = yield select(getShipperOrderAssignmentId);
  const shipperOrder = (journey.shipper_orders || []).find(({ id }) => shipperOrderId === id);
  const { driver, vehicle, vehicles } = payload;

  yield put(setActionLoading(true));
  try {
    yield call(assignShipperOrderRequest, shipperOrder, driver, vehicle, vehicles, {
      break_journey: true,
    });
  } catch (err) {
    yield call(http.getError, err);
  } finally {
    yield put(setShipperOrderAssignmentId(null));
    yield put(setActionLoading(false));
    yield put(closeDecisionModal());
  }
}

function* handleShipperOrderAssignmentClose() {
  yield put(setShipperOrderAssignmentId(null));
}

const changeCostTrackeableEvent = new TrackeableEvent();

function* handleOpenEditCost({ payload }) {
  const journey = yield call(getDetailData);

  const { id: journeyId, status: journeyStatus } = journey;

  yield call(changeCostTrackeableEvent.start, CHANGE_COST_OPEN_MODAL, {
    journeyId,
    journeyStatus,
  });

  yield put(openSubmodal(payload));
}

function* handleCloseEditCost({ payload }) {
  const journey = yield call(getDetailData);

  const { id: journeyId } = journey;

  yield call(changeCostTrackeableEvent.end, CHANGE_COST_CLOSE_MODAL, {
    journeyId,
  });

  yield put(openBaseModal());
}

function* handleSaveEditCost({ payload }) {
  const journey = yield call(getDetailData);

  const { id: journeyId } = journey;

  yield call(changeCostTrackeableEvent.end, CHANGE_COST_SAVE, {
    journeyId,
  });

  yield put(openBaseModalWithUpdate(payload));
}

function* handleRepeatOrder({ payload }) {
  const { id } = payload;
  yield put(openFormWithShipperOrder({ id }));
}

function* handleEditOrder({ payload }) {
  yield call(ShipperOrderTracking.start, payload.id, SHIPPER_ORDER_UPDATE_START);
  yield call(openEditOrder, { payload });
}

function* handlePageOpen({ payload }) {
  yield call(openPage, { payload });
}

function* handleFetchMinervaInfo() {
  const journey = yield call(getDetailData);
  const { id } = journey || {};
  if (!id) return;
  try {
    const minervaInfo = yield call(http.get, `${config.API_URL}mercury/offers/${id}/info/`);
    yield put(setMinervaInfo({ data: minervaInfo, error: null }));
  } catch (e) {
    console.error(e);
    yield put(setMinervaInfo({ data: null, error: 'error' }));
  }
}

function* handleAssignDriver() {
  const journey = yield call(getDetailData);

  yield call(JourneyTracking.start, journey.id, JOURNEY_UPDATE_START, {
    actionType: 'assign_driver',
  });

  yield put(openAssignment());
}

function* handleChangeDriver() {
  const journey = yield call(getDetailData);

  yield call(JourneyTracking.start, journey.id, JOURNEY_UPDATE_START, {
    actionType: 'change_driver',
  });

  yield put(openAssignment());
}

function* handleChangeVehicle() {
  const journey = yield call(getDetailData);

  yield call(JourneyTracking.start, journey.id, JOURNEY_UPDATE_START, {
    actionType: 'change_vehicle',
  });

  yield put(openAssignment());
}

function* handleSelectVehicle() {
  const journey = yield call(getDetailData);

  yield call(JourneyTracking.start, journey.id, JOURNEY_UPDATE_START, {
    actionType: 'select_vehicle',
  });

  yield put(openAssignment());
}

function* handleCloseAssignmentModal() {
  const journey = yield call(getDetailData);

  yield call(JourneyTracking.end, journey.id, JOURNEY_UPDATE_END, {
    actionType: 'close',
  });

  yield put(openBaseModal());
}

function* handleAtomicUpdate({ payload }) {
  const journey = yield call(getDetailData) || {};
  const { id } = payload;

  if (!journey || !journey.id || !id || journey.id !== id) {
    return;
  }

  const newJourney = { ...journey, ...payload };

  const computedJourney = yield call(computeJourney, newJourney);
  yield put(setData(computedJourney));
}

const conversationTypes = {
  offer: ({ driverId }) => ({
    subject: 'OFFER',
    driver_id: driverId,
  }),
  planning: ({ driverId }) => ({
    subject: 'PLANNING',
    driver_id: driverId,
  }),
  billing: ({ driverId }) => ({
    subject: 'BILLING',
    driver_id: driverId,
  }),
  management: ({ driverId }) => ({
    subject: 'MANAGE_ACCOUNT',
    driver_id: driverId,
  }),
  legal: ({ driverId }) => ({
    subject: 'DOCS',
    driver_id: driverId,
  }),
  other: ({ driverId }) => ({
    subject: 'OTHER_CONVERSATION',
    driver_id: driverId,
  }),
};

function* handleCreateChat({ payload }) {
  const { journeyId, driverId, type } = payload;

  try {
    const requestParameters =
      conversationTypes[type] && conversationTypes[type]({ journeyId, driverId });

    if (!requestParameters) return;

    const response = yield call(
      http.post,
      `${config.API_URL}ticketing/conversations/`,
      requestParameters
    );
    const { id: zendeskTicketId } = response;

    yield call(window.open, `${config.ZENDESK_URL}/agent/tickets/${zendeskTicketId}`);

    yield call(sendEventToSegment, CREATE_CONVERSATION, { subject: requestParameters.subject });
  } catch (e) {
    console.error(e);
  }
}

// Watchers
export function* watchJobDetail() {
  yield takeLatest(detailMounted$, handleMounted);

  yield takeLatest(openSubmodal$, submodal);
  yield takeLatest(openBaseModalWithUpdate$, openBaseAndUpdate);
  yield takeLatest(detailUnmounted$, handledetailUnmounted);

  yield takeLatest(copyJobDetails$, copyToClipboard);
  yield takeLatest(viewValidVehicles$, goToValidVehicles);
  yield takeLatest(addManualEntryLog$, handleAddManualLogEntry);
  yield takeLatest(editOrder$, handleEditOrder);
  yield takeLatest(repeatOrder$, handleRepeatOrder);
  yield takeLatest(fetchMinervaInfo$, handleFetchMinervaInfo);

  yield takeLatest(
    [openShipper$, openUser$, openCarrier$, openDriver$, viewCarrierVehicles$],
    handlePageOpen
  );

  yield takeLatest(showDecisionModal$, handleDecisionModal);

  yield takeLatest(assignShipperOrderWithBreak$, handleAssignWithBreak);
  yield takeLatest(closeShipperOrderAssignment$, handleShipperOrderAssignmentClose);

  yield takeLatest(openEditCost$, handleOpenEditCost);
  yield takeLatest(openViewCost$, handleOpenEditCost);

  yield takeLatest(closeEditCost$, handleCloseEditCost);
  yield takeLatest(saveEditCost$, handleSaveEditCost);

  yield takeLatest(openEditCost$, handleOpenEditCost);

  yield takeLatest(closeAssigmentModal$, handleCloseAssignmentModal);
  yield takeLatest(assignDriver$, handleAssignDriver);
  yield takeLatest(changeDriver$, handleChangeDriver);
  yield takeLatest(changeVehicle$, handleChangeVehicle);
  yield takeLatest(selectVehicle$, handleSelectVehicle);

  yield takeLatest(openOrCreateChat$, handleCreateChat);
  yield takeLatest(refreshJobDetail$, handleRefreshJobDetail);
  yield takeLatest(updateStopTimes$, handleUpdateStopTimes);

  yield takeEvery(setSelfChangedData$('journey'), handleSelfChanged);

  yield takeEvery(takeAtomicUpdate('journey:bulk_update_handler'), handleAtomicUpdate);
}
